import { useEffect, useState } from "react";
import { api, formatTRY } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, XCircle, Plus, Minus } from "lucide-react";
import { SaleEditDialog } from "@/components/sales/SaleEditDialog";
import { toast } from "sonner";

export const paymentLabel = (s) => s.payment_type === "Karma" && s.payments
  ? `Karma · Nakit ${formatTRY(s.payments["Nakit"] || 0)} / Kart ${formatTRY(s.payments["Kredi Kartı"] || 0)}` : s.payment_type;

const fmt = (iso, opts) => new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", ...opts });

export const SalesList = ({ sales, onChanged, emptyText = "Henüz satış yok", focusId }) => {
  const [editTarget, setEditTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundQty, setRefundQty] = useState({});
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    if (!focusId || focused === focusId) return;
    const s = sales.find((x) => x.id === focusId);
    if (s) { setFocused(focusId); setEditTarget(s); }
  }, [focusId, sales, focused]);

  const cancelSale = async () => {
    try { await api.delete(`/sales/${cancelTarget.id}`); toast.success("Satış iptal edildi, stok iade edildi"); onChanged(); }
    catch (e) { toast.error("İptal başarısız"); }
    setCancelTarget(null);
  };
  const refundItem = async (idx) => {
    try {
      const r = await api.post(`/sales/${cancelTarget.id}/refund`, { item_index: idx, quantity: refundQty[idx] || 1 });
      toast.success(r.data.message);
      const fresh = await onChanged();
      if (r.data.deleted) setCancelTarget(null);
      else { setCancelTarget((fresh || []).find((s) => s.id === cancelTarget.id) || null); setRefundQty({}); }
    } catch (e) { toast.error("İade başarısız"); }
  };

  return (
    <>
      <div className="space-y-2" data-testid="sales-history">
        {sales.map((s) => (
          <div key={s.id} data-testid={`sale-row-${s.id}`} className={`flex items-center justify-between rounded-lg px-3 py-2 ${s.id === focusId ? "bg-primary/15 ring-1 ring-primary/50" : "bg-secondary/40"}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium">{s.payment_type === "Açık Hesap" && <span className="mr-1 rounded bg-amber-500/20 px-1.5 py-0.5 text-xs font-semibold text-amber-500" data-testid={`sale-open-${s.id}`}>Açık Hesap · Bekliyor</span>}{s.was_open_tab && s.payment_type !== "Açık Hesap" && <span className="mr-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-600" data-testid={`sale-was-open-${s.id}`}>Açık Hesap · Kapandı</span>}{s.customer_name && <span className="mr-1 rounded bg-primary/15 px-1.5 py-0.5 text-xs font-semibold text-primary" data-testid={`sale-customer-${s.id}`}>{s.customer_name}</span>}{s.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ")}</p>
              <p className="text-xs text-muted-foreground">{fmt(s.created_at)} · {paymentLabel(s)}{s.was_open_tab && s.opened_at && s.payment_type !== "Açık Hesap" ? ` · açıldı ${fmt(s.opened_at)}` : ""}{s.discount_amount > 0 && s.payment_type !== "Personel" ? ` · İndirim −${formatTRY(s.discount_amount)}` : ""}{s.created_by ? ` · ${s.created_by}` : ""}
                {s.edited_at && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-500" data-testid={`sale-edited-${s.id}`} title={`${s.edited_by || "—"} · ${fmt(s.edited_at)}`}>
                    <Pencil className="h-2.5 w-2.5" /> düzenlendi · {s.edited_by || "—"} · {fmt(s.edited_at, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div className="text-right">
                <p className="font-heading font-bold text-primary">{formatTRY(s.total)}</p>
                <p className="text-xs text-muted-foreground">kâr {formatTRY(s.profit)}</p>
              </div>
              <Button size="icon" variant="ghost" title="Fişi düzenle" data-testid={`edit-sale-${s.id}`} onClick={() => setEditTarget(s)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title="İade / İptal" data-testid={`cancel-sale-${s.id}`} onClick={() => { setCancelTarget(s); setRefundQty({}); }}><XCircle className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
        {sales.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      </div>

      {editTarget && <SaleEditDialog sale={editTarget} onClose={() => setEditTarget(null)} onSaved={onChanged} />}

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-sale-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>İade / İptal</DialogTitle>
            <DialogDescription>
              {cancelTarget && `${fmt(cancelTarget.created_at)} · ${paymentLabel(cancelTarget)} · Fiş toplamı ${formatTRY(cancelTarget.total)}`}
              <br />Tek bir ürünü iade edebilir veya tüm fişi iptal edebilirsiniz. İade edilen malzemeler stoğa geri eklenir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="refund-items">
            {cancelTarget?.items.map((it, idx) => {
              const q = Math.min(refundQty[idx] || 1, it.quantity);
              return (
                <div key={`${it.recipe_id}-${idx}`} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{it.product_name}</p>
                    <p className="text-xs text-muted-foreground">{it.quantity} adet · {formatTRY(it.unit_price)}</p>
                  </div>
                  {it.quantity > 1 && (
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`refund-dec-${idx}`} onClick={() => setRefundQty({ ...refundQty, [idx]: Math.max(1, q - 1) })}><Minus className="h-3 w-3" /></Button>
                      <span className="w-5 text-center font-mono text-sm" data-testid={`refund-qty-${idx}`}>{q}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`refund-inc-${idx}`} onClick={() => setRefundQty({ ...refundQty, [idx]: Math.min(it.quantity, q + 1) })}><Plus className="h-3 w-3" /></Button>
                    </div>
                  )}
                  <Button size="sm" variant="secondary" data-testid={`refund-item-${idx}`} onClick={() => refundItem(idx)}>İade ({formatTRY(it.unit_price * q)})</Button>
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" data-testid="cancel-sale-dismiss" onClick={() => setCancelTarget(null)}>Kapat</Button>
            <Button variant="destructive" data-testid="cancel-sale-confirm" onClick={cancelSale}>Tüm Fişi İptal Et</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
